import { inject, Injectable, signal } from '@angular/core';
import { ChatRole, MessageContent } from '../models/agent.models';
import { ConversationService } from './conversation.service';
import { USER_DATA_EXCHANGE, UserDataExchangeAbstractService } from '../models/user-data-exchange';
import { WordData } from './popup.service';
import { buildObjectTTSRequest, extractAudioAndTranscription } from '../components/chat-container/chat-messages-list/chat-message/chat-message.utils';
import { ChatMonitorService } from './chat-monitor.service';
import { ChatUserSettings } from '@dataclouder/ngx-core';

@Injectable()
export class MessageOrchestrationService {
  // Services
  private conversationService = inject(ConversationService);
  private userDataExchange = inject<UserDataExchangeAbstractService>(USER_DATA_EXCHANGE);
  private chatMonitorService = inject(ChatMonitorService);

  // State Signals
  private messages = signal<MessageContent[]>([]);
  private messageRole = signal<ChatRole>(ChatRole.User);
  private messageToPlay = signal<MessageContent | null>(null);

  // Public Signals for components to consume
  public messagesSignal = this.messages.asReadonly();
  public messageToPlay$ = this.messageToPlay.asReadonly();

  // Audio queue management
  private audioQueue: number[] = [];
  private isGenerating = signal(false);
  private currentPlayingIndex = signal<number | null>(null);
  private preGenerationInProgress = signal(false);

  constructor() {
    console.log('MessageOrchestrationService initialized');
  }

  public startOrchestration(messages: MessageContent[], role: ChatRole): void {
    this.messages.set([...messages]);
    this.messageRole.set(role);

    if (role === ChatRole.Assistant) {
      const chatSettings = this.userDataExchange.getUserChatSettings();
      if (chatSettings.synthVoice) {
        this.initializeAudioQueue();
        this.processNextInQueue();
      }
    } else {
      this.changeStates(0, this.messages()[0]);
    }
  }

  public audioCompleted(message: MessageContent): void {
    this.conversationService.currentAudioStatus.set({ message, completed: true });
    this.currentPlayingIndex.set(null);
    this.messageToPlay.set(null);
    if (this.audioQueue.length > 0) {
      this.processNextInQueue();
    }
  }

  public wordClicked(wordData: WordData): void {
    this.conversationService.notifyWordClicked(wordData);
  }

  private initializeAudioQueue(): void {
    const messages = this.messages();
    if (messages && messages.length > 0) {
      this.audioQueue = messages.map((_, index) => index);
    }
  }

  private async processNextInQueue(): Promise<void> {
    if (this.audioQueue.length === 0 || this.isGenerating()) {
      return;
    }
    const nextIndex = this.audioQueue.shift()!;
    this.isGenerating.set(true);
    try {
      await this.generateAudioForIndex(nextIndex);
    } catch (error) {
      console.error('Error generating audio:', error);
    } finally {
      this.isGenerating.set(false);
    }
    this.preGenerateNextIfNeeded();
  }

  private async preGenerateNextIfNeeded(): Promise<void> {
    if (this.audioQueue.length > 0 && !this.preGenerationInProgress()) {
      const nextIndex = this.audioQueue[0];
      this.preGenerationInProgress.set(true);
      const messages = this.messages();
      const message = { ...messages[nextIndex], isLoading: true };
      this.changeStates(nextIndex, message);
      const messageAudio = await this.generateAudio(messages[nextIndex], null);
      messageAudio.isLoading = false;
      messageAudio.shouldPlayAudio = false;
      this.changeStates(nextIndex, messageAudio);
      this.preGenerationInProgress.set(false);
    }
  }

  private async generateAudioForIndex(index: number): Promise<void> {
    const messages = this.messages();
    if (messages[index].audioUrl) {
      const messageAudio = { ...messages[index], shouldPlayAudio: true };
      this.changeStates(index, messageAudio);
      this.chatMonitorService.logMessageAudioWillPlay(messageAudio);
      this.messageToPlay.set(messageAudio);
      this.currentPlayingIndex.set(index);
      return;
    }
    const message = { ...messages[index], isLoading: true };
    this.changeStates(index, message);
    try {
      const messageAudio = await this.generateAudio(messages[index], null);
      messageAudio.isLoading = false;
      messageAudio.shouldPlayAudio = true;

      this.changeStates(index, messageAudio);
      this.chatMonitorService.logMessageAudioWillPlay(messageAudio);
      this.messageToPlay.set(messageAudio);
    } finally {
      const message = { ...messages[index], isLoading: false };
      this.changeStates(index, message);
    }
    this.currentPlayingIndex.set(index);
  }

  private changeStates(index: number, messageAudio: MessageContent): void {
    this.messages.update((messages) => {
      const newMessages = [...messages];
      newMessages[index] = { ...messages[index], ...messageAudio };
      return newMessages;
    });
  }

  private async generateAudio(message: MessageContent, overwriteText: string | null = null): Promise<MessageContent> {
    try {
      const text = overwriteText || message.text || message.content;
      const ttsObject = buildObjectTTSRequest({ ...message, text }, { highlightWords: true } as ChatUserSettings);
      const speechAudio = await this.conversationService.getTTSFile(ttsObject);
      message = extractAudioAndTranscription(message, speechAudio);
      return message;
    } finally {
      this.isGenerating.set(false);
    }
  }
}
